import { Component, OnInit, Optional, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-ami-reporting',
  templateUrl: './ami-reporting.component.html',
  styleUrls: ['./ami-reporting.component.css']
})
export class AmiReportingComponent implements OnInit {
  amiReportingForm: FormGroup;
  actionType: 'create' | 'edit' = 'create';
  showProgressSpinner = false;
  successMessage: string;
  errorMessage: string;
  selectedEnvironment: 'live' | 'testing' | 'none' | null = null;

  // Environment presets
  readonly livePreset = {
    amiAddress: 'https://reporting.arthurmurray.com',
    clientId: '63bf070dd57a044bd6009c5b_5uciusmnlbswwkcw0gc04ksos0cg0ws48s8sc4cog804ggwwc0',
    clientSecret: '2m1uytgiee0wsw4co04gw88ockw4gs084g4k044gss4cw8scss',
    grantType: 'password'
  };

  readonly testingPreset = {
    amiAddress: 'https://api.arthurmurrayfranchisee.com',
    clientId: '63bf072c7671e4507d080ae6_17wyf2h6h3r4w8wgwcowc00swc0scsck4884os8404okgwc4ss',
    clientSecret: '22h10dyi20sgskgo00c8oo4gcgwwco0c0gc0ogs4gwcs0880w4',
    grantType: 'password'
  };

  constructor(
    private fb: FormBuilder,
    private drupalRESTService: DrupalRESTService,
    private _permissionsService: PermissionsService,
    @Optional() public dialogRef: MatDialogRef<AmiReportingComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any
  ) {}

  ngOnInit(): void {
    this.amiReportingForm = this.fb.group({
      amiAddress: ['', Validators.required],
      clientId: ['', Validators.required],
      grantType: ['', Validators.required],
      clientSecret: ['', Validators.required],
      username: ['', Validators.required],
      password: ['', Validators.required]
    });

    this.loadAMIReportingConfig();
  }

  loadAMIReportingConfig() {
    this.showProgressSpinner = true;
    const configFieldName = 'field_ami_reporting';
    this.drupalRESTService
      .httpGET(`/api_rest/v1/loadStudioConfig?config_field_name=${configFieldName}`)
      .subscribe(
        (response) => {
          // Transform the response to match the form structure
          const formData = {
            amiAddress: response?.['field_ami_address']?.[0]?.value,
            clientId: response?.['field_client_id']?.[0]?.value,
            grantType: response?.['field_grant_type']?.[0]?.value,
            clientSecret: response?.['field_client_secret']?.[0].value,
            username: response?.['field_user_name']?.[0]?.value, // Note the field name here
            password: response?.['field_password']?.[0]?.value,
          };

          this.amiReportingForm.patchValue(formData);
          this.showProgressSpinner = false;
        },
        (error) => {
          LoggerService.error('Error loading AMI Reporting config:', error);
          this.errorMessage = 'Error loading AMI Reporting configuration.';
          this.showProgressSpinner = false;
        }
      );
  }

  saveAMIReportingConfig() {
    const configFieldName = 'field_ami_reporting';
    const formValues = this.amiReportingForm.value;

    // Transform the form values to match the Drupal expected structure
    const postData = {
      config_field_name: configFieldName,
      config_data: {
        field_ami_address: [{value: formValues.amiAddress}],
        field_client_id: [{value: formValues.clientId}],
        field_grant_type: [{value: formValues.grantType}],
        field_client_secret: [{value: formValues.clientSecret}],
        field_user_name: [{value: formValues.username}], // Note the corrected field name
        field_password: [{value: formValues.password}],
      }
    };

    this.showProgressSpinner = true;
    this.successMessage = '';
    this.errorMessage = '';

    this.drupalRESTService
      .httpPOST('/api_rest/v1/saveStudioConfig', postData)
      .subscribe(
        (response) => {
          LoggerService.log('AMI Reporting config saved successfully:', response);
          this.successMessage = 'AMI Reporting configuration saved successfully.';
          this.showProgressSpinner = false;

          // Close dialog immediately after successful save if in dialog mode
          if (this.dialogRef) {
            this.dialogRef.close(true);
          }
        },
        (error) => {
          LoggerService.error('Error saving AMI Reporting config:', error);
          this.errorMessage = 'Error saving AMI Reporting configuration.';
          this.showProgressSpinner = false;
        }
      );
  }

  onSubmit() {
    this.saveAMIReportingConfig();
  }

  onUpdate() {
    this.saveAMIReportingConfig();
  }

  hasAdminAccess(): boolean {
    return this._permissionsService.has_access('view_admin_links');
  }

  applyEnvironmentPreset(environment: 'live' | 'testing' | 'none'): void {
    this.selectedEnvironment = environment;

    if (environment === 'none') {
      // Don't apply any preset, just set the state
      return;
    }

    const preset = environment === 'live' ? this.livePreset : this.testingPreset;

    // Apply preset values, keeping username and password
    this.amiReportingForm.patchValue({
      amiAddress: preset.amiAddress,
      clientId: preset.clientId,
      clientSecret: preset.clientSecret,
      grantType: preset.grantType
    });
  }

  isFieldReadonly(fieldName: string): boolean {
    // Username and password are always editable (if user has admin access)
    if (fieldName === 'username' || fieldName === 'password') {
      return !this.hasAdminAccess();
    }

    // Other fields are readonly when live or testing is selected, or user lacks admin access
    // If 'none' is selected, fields should be editable (if user has admin access)
    return (this.selectedEnvironment === 'live' || this.selectedEnvironment === 'testing') || !this.hasAdminAccess();
  }

  clearEnvironmentSelection(): void {
    this.selectedEnvironment = null;
  }
}
